import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import styles from './AdminEnterpriseDashboard.module.css';

/* ============================================================
   MOCK DATA — placeholder only, per spec. Replace with API data
   once a backend endpoint exists.
   One record per captured folder:
     { id, type, customer, created: Date, ep, h, rh, status,
       files: { mp4, sov2, mcap } }
   `type` indexes into SCENARIOS; `customer` indexes into
   CUSTOMERS, or -1 for unsold capture sitting in the data bank.
   `status` is "accepted" or "rejected" for the whole folder;
   `rh` is its rejected hours, so rh === h when rejected, else 0.
   ============================================================ */
const SCENARIOS = ['Shirt folding', 'Pick & place', 'Cup stacking', 'Drawer open', 'Wiping table'];
const CUSTOMERS = ['Northgate Robotics', 'Helio Labs', 'Kaisen AI', 'Vector Dynamics', 'Orbital Systems'];
const DATA_BANK = -1;
const HISTORY_DAYS = 91;
const PAGE = 60;

function mulberry32(a) {
    return function () {
        a |= 0; a = a + 0x6D2B79F5 | 0;
        let t = Math.imul(a ^ a >>> 15, 1 | a);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
}

const FOLDERS = (function buildMock() {
    const rnd = mulberry32(20260809);
    const hex8 = () => Math.floor(rnd() * 0xffffffff).toString(16).padStart(8, '0');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const base = [46, 38, 26, 30, 22];
    const avgMin = [3.1, 2.4, 1.9, 1.6, 2.8];
    const rejBase = [0.09, 0.12, 0.16, 0.07, 0.11];
    const out = [];
    for (let i = HISTORY_DAYS - 1; i >= 0; i--) {
        const d = new Date(today); d.setDate(d.getDate() - i);
        const dow = d.getDay();
        const weekend = (dow === 0 || dow === 6) ? 0.45 : 1;
        const growth = 0.55 + 0.45 * ((HISTORY_DAYS - i) / HISTORY_DAYS);
        const partial = (i === 0) ? 0.5 : 1;
        SCENARIOS.forEach((_, s) => {
            const nF = 1 + Math.round(rnd() * 1.4);
            for (let f = 0; f < nF; f++) {
                const ep = Math.max(1, Math.round(base[s] * weekend * growth * partial * (0.7 + rnd() * 0.6) / nF));
                const h = +(ep * avgMin[s] * (0.9 + rnd() * 0.2) / 60).toFixed(2);
                const rejected = rnd() < rejBase[s];
                const rh = rejected ? h : 0;
                const created = new Date(d);
                created.setHours(8 + Math.floor(rnd() * 12), Math.floor(rnd() * 60), 0, 0);
                const customer = rnd() < 0.3 ? DATA_BANK : Math.floor(rnd() * CUSTOMERS.length);
                const stereo = rnd() < 0.75;
                const files = {
                    mp4: ep * 3 - (rnd() < 0.06 ? 1 : 0),
                    sov2: stereo ? ep - (rnd() < 0.04 ? 1 : 0) : 0,
                    mcap: ep,
                };
                out.push({
                    id: 'fld_' + hex8(), type: s, customer, created, ep, h, rh, files,
                    status: rejected ? 'rejected' : 'accepted',
                });
            }
        });
    }
    return out.sort((a, b) => b.created - a.created);
})();
/* ===================== END MOCK DATA ===================== */

const FILTER_GROUPS = [
    {
        key: 'type', title: 'Type', allLabel: 'All types', manyLabel: 'Types',
        opts: SCENARIOS.map((n, i) => ({ v: i, n })), of: (f) => f.type,
    },
    {
        key: 'customer', title: 'Customer', allLabel: 'All customers', manyLabel: 'Customers',
        opts: CUSTOMERS.map((n, i) => ({ v: i, n })).concat([{ v: DATA_BANK, n: 'Data bank' }]), of: (f) => f.customer,
    },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtNum = (n) => n.toLocaleString('en-US');
const fmtHrs = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtPct = (n) => (n * 100).toFixed(1) + '%';
const shortDate = (d) => MONTHS[d.getMonth()] + ' ' + d.getDate();
const dayKey = (d) => d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate();
const fmtTime = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const customerName = (c) => (c === DATA_BANK ? 'Data bank' : CUSTOMERS[c]);

const FIRST_CAPTURE = (function () {
    const d = FOLDERS.reduce((a, f) => (f.created < a ? f.created : a), FOLDERS[0].created);
    const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
})();

const SPAN_PRESETS = [
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
    { key: 'all', label: 'All time' },
];
const SPAN_MONTHS = (function () {
    const m = new Map();
    FOLDERS.forEach((f) => {
        const k = f.created.getFullYear() + '-' + f.created.getMonth();
        if (!m.has(k)) m.set(k, new Date(f.created.getFullYear(), f.created.getMonth(), 1));
    });
    return [...m.values()].sort((a, b) => b - a).slice(0, 12).map((d) => ({
        key: 'm' + d.getFullYear() + '-' + d.getMonth(),
        label: MONTHS[d.getMonth()] + ' ' + d.getFullYear(),
        date: d,
    }));
})();
const SPANS = SPAN_PRESETS.concat(SPAN_MONTHS);

function spanLabel(key, customFrom, customTo) {
    if (key === 'custom') {
        return customFrom && customTo ? shortDate(customFrom) + ' – ' + shortDate(customTo) : 'Custom range';
    }
    return (SPANS.find((x) => x.key === key) || SPANS[1]).label;
}
const toISODate = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
const fromISODate = (v) => { const [y, m, d] = v.split('-').map(Number); return new Date(y, m - 1, d); };
const dayCount = (a, b) => Math.round((b - a) / 86400000) + 1;
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

function spanRange(key, customFrom, customTo) {
    const today = new Date(); today.setHours(0, 0, 0, 0);

    if (key === 'custom' && customFrom && customTo) {
        const n = dayCount(customFrom, customTo);
        const pFrom = new Date(customFrom); pFrom.setDate(pFrom.getDate() - n);
        const pTo = new Date(customFrom.getTime() - 1);
        return {
            from: customFrom, to: endOfDay(customTo),
            prev: { from: pFrom, to: pTo, label: 'previous ' + n + (n === 1 ? ' day' : ' days') },
            rangeText: shortDate(customFrom) + ' – ' + shortDate(customTo) + ', ' + customTo.getFullYear() +
                ' · ' + n + (n === 1 ? ' day' : ' days'),
        };
    }

    if (key === 'all') {
        return {
            from: new Date(0), to: endOfDay(today), prev: null,
            rangeText: shortDate(FIRST_CAPTURE) + ' – ' + shortDate(today) + ', ' + today.getFullYear(),
        };
    }

    if (key[0] === 'm') {
        const [yr, mo] = key.slice(1).split('-').map(Number);
        const from = new Date(yr, mo, 1);
        const to = endOfDay(new Date(yr, mo + 1, 0));
        const toDate = to > today;
        const last = toDate ? today : to;
        const pFrom = new Date(yr, mo - 1, 1);
        const pTo = toDate
            ? endOfDay(new Date(yr, mo - 1, Math.min(last.getDate(), new Date(yr, mo, 0).getDate())))
            : endOfDay(new Date(yr, mo, 0));
        const pName = MONTHS[pFrom.getMonth()];
        return {
            from, to,
            prev: { from: pFrom, to: pTo, label: toDate ? pName + ' 1–' + pTo.getDate() : pName + ' ' + pFrom.getFullYear() },
            rangeText: shortDate(from) + ' – ' + shortDate(last) + ', ' + yr + (toDate ? ' · month to date' : ''),
        };
    }

    const n = parseInt(key, 10);
    const from = new Date(today); from.setDate(from.getDate() - (n - 1));
    const pFrom = new Date(today); pFrom.setDate(pFrom.getDate() - (2 * n - 1));
    const pTo = new Date(from.getTime() - 1);
    return {
        from, to: endOfDay(today), prev: { from: pFrom, to: pTo, label: 'previous ' + n + ' days' },
        rangeText: shortDate(from) + ' – ' + shortDate(today) + ', ' + today.getFullYear(),
    };
}

function sumF(list) {
    return list.reduce((a, f) => ({ ep: a.ep + f.ep, h: a.h + f.h, rh: a.rh + f.rh }), { ep: 0, h: 0, rh: 0 });
}
function startOfWeek(d) {
    const x = new Date(d); const dow = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - dow); x.setHours(0, 0, 0, 0); return x;
}
const DAY_LIST = (function () {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const out = [];
    for (let i = HISTORY_DAYS - 1; i >= 0; i--) {
        const d = new Date(today); d.setDate(d.getDate() - i);
        out.push(d);
    }
    return out;
})();

function dayTotalsMap(list) {
    const m = new Map();
    list.forEach((f) => {
        const k = dayKey(f.created);
        const b = m.get(k) || { ep: 0, h: 0, rh: 0 };
        b.ep += f.ep; b.h += f.h; b.rh += f.rh;
        m.set(k, b);
    });
    return m;
}

function chartSeries(list, grain) {
    const m = dayTotalsMap(list);
    const days = DAY_LIST.map((d) => {
        const t = m.get(dayKey(d)) || { ep: 0, h: 0, rh: 0 };
        return { date: d, ...t };
    });

    if (grain === 'month') {
        const mm = new Map();
        days.forEach((d) => {
            const k = d.date.getFullYear() + '-' + d.date.getMonth();
            if (!mm.has(k)) mm.set(k, { date: new Date(d.date.getFullYear(), d.date.getMonth(), 1), ep: 0, h: 0, rh: 0, days: 0 });
            const b = mm.get(k);
            b.ep += d.ep; b.h += d.h; b.rh += d.rh; b.days++;
        });
        return [...mm.values()].sort((a, b) => a.date - b.date).slice(-12).map((b) => {
            const inMonth = new Date(b.date.getFullYear(), b.date.getMonth() + 1, 0).getDate();
            return {
                key: MONTHS[b.date.getMonth()] + ' ' + b.date.getFullYear(),
                short: MONTHS[b.date.getMonth()],
                ep: b.ep, h: +b.h.toFixed(2), rh: +b.rh.toFixed(2), acc: +(b.h - b.rh).toFixed(2),
                partial: b.days < inMonth,
            };
        });
    }

    if (grain === 'week') {
        const wm = new Map();
        days.forEach((d) => {
            const k = startOfWeek(d.date).getTime();
            if (!wm.has(k)) wm.set(k, { date: new Date(k), ep: 0, h: 0, rh: 0, days: 0 });
            const b = wm.get(k);
            b.ep += d.ep; b.h += d.h; b.rh += d.rh; b.days++;
        });
        return [...wm.values()].sort((a, b) => a.date - b.date).slice(-12).map((b) => ({
            key: 'Week of ' + shortDate(b.date), short: shortDate(b.date),
            ep: b.ep, h: +b.h.toFixed(2), rh: +b.rh.toFixed(2), acc: +(b.h - b.rh).toFixed(2),
            partial: b.days < 7,
        }));
    }

    return days.slice(-30).map((d) => ({
        key: shortDate(d.date), short: shortDate(d.date), ep: d.ep,
        h: +d.h.toFixed(2), rh: +d.rh.toFixed(2), acc: +(d.h - d.rh).toFixed(2), partial: false,
    }));
}

function toCSV(list) {
    const head = ['folder_id', 'type', 'customer', 'created_time', 'status',
        'episodes', 'mp4_files', 'sov2_files', 'mcap_files', 'hours'];
    const esc = (c) => { const t = String(c); return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };
    const rows = list.map((f) => [
        f.id, SCENARIOS[f.type], customerName(f.customer),
        f.created.toISOString(), f.status, f.ep,
        f.files.mp4, f.files.sov2, f.files.mcap, f.h.toFixed(2),
    ]);
    return [head, ...rows].map((r) => r.map(esc).join(',')).join('\n');
}

/* ── Small presentational helpers ── */

function DeltaText({ value, invert, note }) {
    if (!isFinite(value)) return <>no prior data</>;
    if (Math.abs(value) < 0.005) return <><span className={styles.deltaFlat}>no change</span> {note}</>;
    const good = invert ? value < 0 : value > 0;
    const sign = value > 0 ? '+' : '−';
    return (
        <>
            <span className={good ? styles.deltaUp : styles.deltaDown}>{sign}{Math.abs(value * 100).toFixed(0)}%</span> {note}
        </>
    );
}

const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
        <div className={styles.chartTip}>
            <div className={styles.chartTipHead}>
                {d.key}{d.partial ? <span className={styles.chartTipPartial}> · partial</span> : null}
            </div>
            <div className={styles.chartTipRow}><span>Episodes</span><b>{fmtNum(d.ep)}</b></div>
            <div className={`${styles.chartTipRow} ${styles.chartTipAcc}`}><span>Accepted</span><b>{fmtHrs(d.acc)}h</b></div>
            <div className={`${styles.chartTipRow} ${styles.chartTipRej}`}><span>Rejected</span><b>{fmtHrs(d.rh)}h</b></div>
            <div className={styles.chartTipRow}><span>Total</span><b>{fmtHrs(d.h)}h</b></div>
        </div>
    );
};

/* ── Main component ── */

const AdminEnterpriseDashboard = () => {
    const [selections, setSelections] = useState({ type: new Set(), customer: new Set() });
    const [grain, setGrain] = useState('day');
    const [spanKey, setSpanKey] = useState('30d');
    const [customFrom, setCustomFrom] = useState(null);
    const [customTo, setCustomTo] = useState(null);
    const [shown, setShown] = useState(PAGE);
    const [openPopover, setOpenPopover] = useState(null); // null | 'type' | 'customer' | 'span'
    const [spanFromInput, setSpanFromInput] = useState('');
    const [spanToInput, setSpanToInput] = useState('');
    const [updatedAt] = useState(() => new Date());

    const controlsRef = useRef(null);

    useEffect(() => {
        function handleDocClick(e) {
            if (controlsRef.current && !controlsRef.current.contains(e.target)) {
                setOpenPopover(null);
            }
        }
        function handleKey(e) {
            if (e.key === 'Escape') setOpenPopover(null);
        }
        document.addEventListener('mousedown', handleDocClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleDocClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, []);

    useEffect(() => {
        if (openPopover === 'span') {
            const r = spanRange(spanKey, customFrom, customTo);
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const from = r.from.getTime() === 0 ? FIRST_CAPTURE : r.from;
            const to = r.to > today ? today : r.to;
            setSpanFromInput(toISODate(from));
            setSpanToInput(toISODate(to));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openPopover]);

    const toggleOption = useCallback((key, v) => {
        setSelections((prev) => {
            const next = new Set(prev[key]);
            next.has(v) ? next.delete(v) : next.add(v);
            return { ...prev, [key]: next };
        });
        setShown(PAGE);
    }, []);

    const clearGroup = useCallback((key) => {
        setSelections((prev) => ({ ...prev, [key]: new Set() }));
        setShown(PAGE);
    }, []);

    const activeFolders = useMemo(() => {
        const activeGroups = FILTER_GROUPS.filter((g) => selections[g.key].size);
        if (!activeGroups.length) return FOLDERS;
        return FOLDERS.filter((f) => activeGroups.every((g) => selections[g.key].has(g.of(f))));
    }, [selections]);

    const filterSummary = useMemo(() => {
        return FILTER_GROUPS
            .filter((g) => selections[g.key].size)
            .map((g) => [...selections[g.key]].map((v) => g.opts.find((o) => o.v === v).n).join(', '))
            .join(' · ');
    }, [selections]);

    const kpis = useMemo(() => {
        const r = spanRange(spanKey, customFrom, customTo);
        const inRange = (f, a, b) => f.created >= a && f.created <= b;
        const cur = activeFolders.filter((f) => inRange(f, r.from, r.to));
        const comparable = r.prev && r.prev.from >= FIRST_CAPTURE;
        const prev = comparable ? activeFolders.filter((f) => inRange(f, r.prev.from, r.prev.to)) : null;

        const a = sumF(cur);
        const rate = a.h ? a.rh / a.h : 0;
        const dPct = (x, y) => (y ? (x - y) / y : NaN);

        let subEp, subH, subRh, subRate;
        if (prev) {
            const b = sumF(prev);
            const bRate = b.h ? b.rh / b.h : 0;
            const note = 'vs. ' + r.prev.label;
            subEp = <DeltaText value={dPct(a.ep, b.ep)} invert={false} note={note} />;
            subH = <DeltaText value={dPct(a.h, b.h)} invert={false} note={note} />;
            subRh = <DeltaText value={dPct(a.rh, b.rh)} invert note={note} />;
            subRate = <DeltaText value={b.h ? rate - bRate : NaN} invert note={note} />;
        } else {
            const rejected = cur.filter((f) => f.status === 'rejected').length;
            subEp = `across ${fmtNum(cur.length)} folders`;
            subH = r.rangeText;
            subRh = `${fmtPct(rate)} of total hours`;
            subRate = `${fmtNum(rejected)} of ${fmtNum(cur.length)} folders rejected`;
        }

        return { ep: a.ep, h: a.h, rh: a.rh, rate, subEp, subH, subRh, subRate, rangeText: r.rangeText };
    }, [activeFolders, spanKey, customFrom, customTo]);

    const chartData = useMemo(() => chartSeries(activeFolders, grain), [activeFolders, grain]);
    const chartDesc = grain === 'month' ? 'Accepted and rejected hours, by month'
        : grain === 'week' ? 'Accepted and rejected hours, last 12 weeks'
            : 'Accepted and rejected hours, last 30 days';

    const tableSlice = activeFolders.slice(0, shown);
    const allLoaded = tableSlice.length >= activeFolders.length;

    const handleDownloadCSV = useCallback(() => {
        if (!activeFolders.length) return;
        const d = new Date();
        const stamp = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        const blob = new Blob([toCSV(activeFolders)], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `prismax-enterprise-raw-data-${stamp}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    }, [activeFolders]);

    const spanInputsValid = spanFromInput && spanToInput && fromISODate(spanFromInput) <= fromISODate(spanToInput);

    const handleApplyCustomSpan = () => {
        if (!spanInputsValid) return;
        setCustomFrom(fromISODate(spanFromInput));
        setCustomTo(fromISODate(spanToInput));
        setSpanKey('custom');
        setOpenPopover(null);
    };

    return (
        <div className={styles.container}>
            {/* TEMPORARY: remove once this dashboard is wired up to real data */}
            <div className={styles.placeholderNotice}>
                Please note that the data displayed is currently placeholder/mock data.
            </div>

            {/* Filters + time span */}
            <div ref={controlsRef}>
                <div className={styles.toolbar}>
                    <div className={styles.filterBar}>
                        {FILTER_GROUPS.map((g) => {
                            const set = selections[g.key];
                            const n = set.size;
                            const label = n === 0 ? g.allLabel : n === 1 ? g.opts.find((o) => o.v === [...set][0]).n : g.manyLabel;
                            return (
                                <div className={styles.filterWrap} key={g.key}>
                                    <button
                                        type="button"
                                        className={styles.filterBtn}
                                        aria-expanded={openPopover === g.key}
                                        onClick={() => setOpenPopover((p) => (p === g.key ? null : g.key))}
                                    >
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M1 2h10M2.5 6h7M4.5 10h3" stroke="rgba(255,255,255,0.55)" strokeWidth="1.3" strokeLinecap="round" />
                                        </svg>
                                        <span>{label}</span>
                                        {n >= 2 && <span className={styles.filterCount}>{n}</span>}
                                    </button>
                                    {openPopover === g.key && (
                                        <div className={styles.filterPop} role="group" aria-label={`Filter by ${g.title.toLowerCase()}`}>
                                            <div className={styles.popHead}>
                                                <span className={styles.popTitle}>{g.title}</span>
                                                <button type="button" className={styles.popClear} onClick={() => clearGroup(g.key)}>Clear</button>
                                            </div>
                                            <div>
                                                {g.opts.map((o) => {
                                                    const count = FOLDERS.filter((f) => g.of(f) === o.v).length;
                                                    const pressed = set.has(o.v);
                                                    return (
                                                        <button
                                                            key={o.v}
                                                            type="button"
                                                            className={styles.popOpt}
                                                            aria-pressed={pressed}
                                                            onClick={() => toggleOption(g.key, o.v)}
                                                        >
                                                            <span className={`${styles.cbox} ${pressed ? styles.cboxOn : ''}`}>
                                                                {pressed && (
                                                                    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                                        <path d="M1 3.6L3.3 6 8 1" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                                                    </svg>
                                                                )}
                                                            </span>
                                                            <span className={styles.popOptLbl}>{o.n}</span>
                                                            <span className={styles.popOptN}>{fmtNum(count)}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <span className={styles.updated}>
                        Updated {updatedAt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                </div>

                <div className={styles.spanRow}>
                    <div className={styles.filterWrap}>
                        <button
                            type="button"
                            className={styles.filterBtn}
                            aria-expanded={openPopover === 'span'}
                            onClick={() => setOpenPopover((p) => (p === 'span' ? null : 'span'))}
                        >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <rect x="1" y="2.5" width="10" height="8.5" rx="1.5" stroke="rgba(255,255,255,0.55)" strokeWidth="1.1" />
                                <path d="M1 5h10M3.5 1v2M8.5 1v2" stroke="rgba(255,255,255,0.55)" strokeWidth="1.1" strokeLinecap="round" />
                            </svg>
                            <span>{spanLabel(spanKey, customFrom, customTo)}</span>
                        </button>
                        {openPopover === 'span' && (
                            <div className={styles.filterPop} role="group" aria-label="Time span">
                                <div className={styles.popHead}><span className={styles.popTitle}>Time span</span></div>
                                <div>
                                    {SPAN_PRESETS.map((o) => (
                                        <button
                                            key={o.key}
                                            type="button"
                                            className={`${styles.popOpt} ${styles.popOptSingle}`}
                                            aria-pressed={spanKey === o.key}
                                            onClick={() => { setSpanKey(o.key); setOpenPopover(null); }}
                                        >
                                            <span className={`${styles.cboxRound} ${spanKey === o.key ? styles.cboxOn : ''}`}>
                                                {spanKey === o.key && (
                                                    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                        <path d="M1 3.6L3.3 6 8 1" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </span>
                                            <span className={styles.popOptLbl}>{o.label}</span>
                                        </button>
                                    ))}
                                    {SPAN_MONTHS.length > 0 && <div className={styles.popDiv} />}
                                    {SPAN_MONTHS.map((o) => (
                                        <button
                                            key={o.key}
                                            type="button"
                                            className={`${styles.popOpt} ${styles.popOptSingle}`}
                                            aria-pressed={spanKey === o.key}
                                            onClick={() => { setSpanKey(o.key); setOpenPopover(null); }}
                                        >
                                            <span className={`${styles.cboxRound} ${spanKey === o.key ? styles.cboxOn : ''}`}>
                                                {spanKey === o.key && (
                                                    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                        <path d="M1 3.6L3.3 6 8 1" stroke="#111" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </span>
                                            <span className={styles.popOptLbl}>{o.label}</span>
                                        </button>
                                    ))}
                                </div>
                                <div className={styles.popDiv} />
                                <div className={styles.popCustom}>
                                    <span className={styles.popTitle}>Custom range</span>
                                    <div className={styles.popCustomRow}>
                                        <input
                                            type="date"
                                            className={styles.dateIn}
                                            aria-label="From date"
                                            min={toISODate(FIRST_CAPTURE)}
                                            max={toISODate(new Date())}
                                            value={spanFromInput}
                                            onChange={(e) => setSpanFromInput(e.target.value)}
                                        />
                                        <span className={styles.popHint}>to</span>
                                        <input
                                            type="date"
                                            className={styles.dateIn}
                                            aria-label="To date"
                                            min={toISODate(FIRST_CAPTURE)}
                                            max={toISODate(new Date())}
                                            value={spanToInput}
                                            onChange={(e) => setSpanToInput(e.target.value)}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.applyBtn}
                                        disabled={!spanInputsValid}
                                        onClick={handleApplyCustomSpan}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <span className={styles.spanRange}>{kpis.rangeText}</span>
                </div>
            </div>

            {/* KPI strip */}
            <div className={styles.kpiRow}>
                <div className={styles.kpi}>
                    <div className={styles.kpiLbl}>Episodes</div>
                    <div className={styles.kpiVal}>{fmtNum(kpis.ep)}</div>
                    <div className={styles.kpiSub}>{kpis.subEp}</div>
                </div>
                <div className={styles.kpi}>
                    <div className={styles.kpiLbl}>Total hours</div>
                    <div className={styles.kpiVal}>{fmtHrs(kpis.h)}<span className={styles.kpiUnit}>h</span></div>
                    <div className={styles.kpiSub}>{kpis.subH}</div>
                </div>
                <div className={styles.kpi}>
                    <div className={styles.kpiLbl}>Rejected hours</div>
                    <div className={`${styles.kpiVal} ${styles.kpiRed}`}>{fmtHrs(kpis.rh)}<span className={styles.kpiUnit}>h</span></div>
                    <div className={styles.kpiSub}>{kpis.subRh}</div>
                </div>
                <div className={styles.kpi}>
                    <div className={styles.kpiLbl}>Rejection rate</div>
                    <div className={`${styles.kpiVal} ${kpis.rate <= 0.12 ? styles.kpiGreen : styles.kpiRed}`}>{fmtPct(kpis.rate)}</div>
                    <div className={styles.kpiSub}>{kpis.subRate}</div>
                </div>
            </div>

            {/* Chart */}
            <div className={styles.card}>
                <div className={styles.cardHead}>
                    <div>
                        <div className={styles.cardTitle}>Hours captured</div>
                        <div className={styles.cardSub}>{chartDesc}</div>
                    </div>
                    <div className={styles.chartHeadRight}>
                        <div className={styles.legend}>
                            <span className={styles.lg}><span className={`${styles.lgSwatch} ${styles.lgAcc}`} />Accepted</span>
                            <span className={styles.lg}><span className={`${styles.lgSwatch} ${styles.lgRej}`} />Rejected</span>
                        </div>
                        <div className={styles.seg}>
                            {['day', 'week', 'month'].map((gKey) => (
                                <button
                                    key={gKey}
                                    type="button"
                                    className={grain === gKey ? styles.segActive : ''}
                                    aria-pressed={grain === gKey}
                                    onClick={() => setGrain(gKey)}
                                >
                                    {gKey === 'day' ? 'Day' : gKey === 'week' ? 'Week' : 'Month'}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className={styles.cardBody}>
                    {chartData.length === 0 ? (
                        <div className={styles.empty}>No data for this selection.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.055)" />
                                <XAxis dataKey="short" tick={{ fill: 'rgba(255,255,255,0.28)', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.08)' }} tickLine={false} />
                                <YAxis tick={{ fill: 'rgba(255,255,255,0.28)', fontSize: 10 }} axisLine={false} tickLine={false} width={36} />
                                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                                <Bar dataKey="acc" name="Accepted" stackId="hrs" fill="#60c87a" fillOpacity={0.85} isAnimationActive={false} />
                                <Bar dataKey="rh" name="Rejected" stackId="hrs" fill="#e05555" radius={[1.5, 1.5, 0, 0]} isAnimationActive={false} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* Raw data table */}
            <div className={`${styles.card} ${styles.tableCard}`}>
                <div className={styles.cardHead}>
                    <div>
                        <div className={styles.cardTitle}>Raw data</div>
                        <div className={styles.cardSub}>{filterSummary ? `Newest first · ${filterSummary}` : 'Every captured folder, newest first'}</div>
                    </div>
                    <button type="button" className={styles.dlBtn} disabled={!activeFolders.length} onClick={handleDownloadCSV}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M6 1v7m0 0L3.4 5.4M6 8l2.6-2.6M1.5 10.5h9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Download CSV
                    </button>
                </div>
                <div className={styles.cardBodyFlush}>
                    <div className={styles.tblScroll}>
                        <table className={styles.tbl}>
                            <thead>
                                <tr>
                                    <th>Folder ID</th>
                                    <th>Type</th>
                                    <th>Customer</th>
                                    <th>Created time</th>
                                    <th>Status</th>
                                    <th className={`${styles.r} ${styles.grp}`}>MP4</th>
                                    <th className={styles.r}>SOV2</th>
                                    <th className={styles.r}>Mcap</th>
                                    <th className={styles.r}>Hrs</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableSlice.length === 0 ? (
                                    <tr><td colSpan={9} className={styles.empty}>No folders match these filters.</td></tr>
                                ) : tableSlice.map((f) => (
                                    <tr key={f.id}>
                                        <td className={styles.id}>{f.id}</td>
                                        <td><span className={styles.typeTag}>{SCENARIOS[f.type]}</span></td>
                                        <td className={`${styles.cust} ${f.customer === DATA_BANK ? styles.custBank : ''}`}>{customerName(f.customer)}</td>
                                        <td>{shortDate(f.created)}, {f.created.getFullYear()} · {fmtTime(f.created)}</td>
                                        <td>
                                            <span className={`${styles.status} ${f.status === 'rejected' ? styles.statusRej : styles.statusAcc}`}>
                                                <span className={styles.statusDot} />{f.status === 'rejected' ? 'Rejected' : 'Accepted'}
                                            </span>
                                        </td>
                                        <td className={`${styles.r} ${styles.n} ${styles.grp} ${f.files.mp4 ? '' : styles.zero}`}>{fmtNum(f.files.mp4)}</td>
                                        <td className={`${styles.r} ${styles.n} ${f.files.sov2 ? '' : styles.zero}`}>{fmtNum(f.files.sov2)}</td>
                                        <td className={`${styles.r} ${styles.n} ${f.files.mcap ? '' : styles.zero}`}>{fmtNum(f.files.mcap)}</td>
                                        <td className={`${styles.r} ${styles.hrs}`}>{f.h.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.tableFoot}>
                        <span className={styles.footCount}>
                            {activeFolders.length ? `Showing ${fmtNum(tableSlice.length)} of ${fmtNum(activeFolders.length)} folders` : '0 folders'}
                        </span>
                        <button
                            type="button"
                            className={styles.moreBtn}
                            disabled={allLoaded}
                            onClick={() => setShown((s) => s + PAGE)}
                        >
                            {allLoaded ? 'All loaded' : 'Load more'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminEnterpriseDashboard;
